import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSmartLanguage } from '@/hooks/useSmartLanguage';

interface LanguageSwitcherProps {
  /** When true, renders a single icon-only square button suited for dense headers. */
  compact?: boolean;
  className?: string;
}

export const LanguageSwitcher = ({ compact = false, className }: LanguageSwitcherProps = {}) => {
  const { currentLanguage, changeLanguage } = useSmartLanguage();

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'pt-BR', name: 'Português (BR)', flag: '🇧🇷' }
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    changeLanguage(langCode as 'en' | 'pt-BR');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Language: ${currentLang.name}`}
            className={cn("h-7 w-7 rounded-full", className)}
          >
            <Globe className="w-3.5 h-3.5" />
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className={cn("gap-2", className)}>
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">{currentLang.flag}</span>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className="cursor-pointer gap-2"
          >
            <span>{language.flag}</span>
            <span>{language.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
